import React, { useState } from 'react';
import { onboardingAPI } from '../api';

/**
 * EditorialGuidance — large, calm, magazine-style guidance block.
 *
 * Used SPARINGLY (1 per page max) at intentional moments to communicate
 * the *transformation* a Maestra studio offers, not feature explanations.
 *
 * Placement keys must match the backend `demo_experience.DEFAULT_GUIDANCE`:
 *   - "demo_intro"     → above the gallery on the demo board
 *   - "library_intro"  → on the Image Library empty state
 *   - "welcome_intro"  → on the dashboard for first-time users
 *
 * Tone: editorial. No "click to upload", no SaaS labels. Almost invisible.
 *
 * Props:
 *   placement       — string (one of the keys above)
 *   guidance        — { [placement]: { [lang]: { title, body } } } from API
 *   lang            — current workspace language ('en' | 'pl' | 'de')
 *   dismissed       — array of placements already dismissed (from /onboarding/state)
 *   onDismissed     — optional callback after server confirms
 *   align           — 'left' (default) | 'center'
 *   maxWidth        — number, default 720
 */
const EditorialGuidance = ({
  placement,
  guidance,
  lang = 'en',
  dismissed = [],
  onDismissed,
  align = 'left',
  maxWidth = 720,
}) => {
  const [hidden, setHidden] = useState(() => dismissed.includes(placement));
  const [animatingOut, setAnimatingOut] = useState(false);

  if (hidden) return null;

  const block = guidance && guidance[placement];
  if (!block) return null;
  const copy = block[lang] || block.en;
  if (!copy || (!copy.title && !copy.body)) return null;

  const dismiss = async () => {
    setAnimatingOut(true);
    setTimeout(() => {
      setHidden(true);
      if (onDismissed) onDismissed(placement);
    }, 320);
    try { await onboardingAPI.dismissGuidance(placement); } catch (e) { /* non-fatal */ }
  };

  const isCenter = align === 'center';

  return (
    <section
      data-testid={`editorial-guidance-${placement}`}
      style={{
        opacity: animatingOut ? 0 : 1,
        transition: 'opacity 0.32s ease',
        margin: '64px auto 56px',
        padding: '0 32px',
        position: 'relative',
        textAlign: isCenter ? 'center' : 'left',
        maxWidth: maxWidth + 64,
      }}
    >
      <button
        type="button"
        onClick={dismiss}
        title="Dismiss"
        data-testid={`editorial-guidance-dismiss-${placement}`}
        style={{
          position: 'absolute',
          top: -4,
          right: 28,
          background: 'none',
          border: 'none',
          padding: 8,
          fontSize: 14,
          lineHeight: 1,
          cursor: 'pointer',
          color: 'var(--kahla-text-light)',
          opacity: 0.32,
          transition: 'opacity 0.2s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = 0.85)}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.32)}
      >
        ×
      </button>

      <div
        style={{
          maxWidth,
          margin: isCenter ? '0 auto' : 0,
        }}
      >
        <h3
          style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: 'clamp(22px, 2.6vw, 30px)',
            fontWeight: 400,
            lineHeight: 1.22,
            letterSpacing: '-0.005em',
            color: 'var(--kahla-charcoal)',
            margin: '0 0 18px',
          }}
          data-testid={`editorial-guidance-title-${placement}`}
        >
          {copy.title}
        </h3>
        <p
          style={{
            fontFamily: 'Cormorant Garamond, Playfair Display, serif',
            fontStyle: 'italic',
            fontSize: 18,
            lineHeight: 1.7,
            color: 'var(--kahla-text-light)',
            margin: 0,
            letterSpacing: '0.005em',
          }}
          data-testid={`editorial-guidance-body-${placement}`}
        >
          {copy.body}
        </p>
      </div>
    </section>
  );
};

export default EditorialGuidance;
